/**
 * Client-side mirror of the kufer volume rules
 * (`internal/module/container/model/volume_model.go`).
 *
 * Every validator returns an error KEY or null — never a rendered string — so this module stays
 * free of React and i18next and runs under `node --test`. Resolve a key with
 * `t(`volumes.validation.${key}`, VOLUME_ERROR_DEFAULTS[key])`.
 *
 * The server is the authority: it re-validates and returns the CLEANED mount path, so render
 * `mount_path` from the response rather than the string the user typed.
 */

export const MIN_VOLUME_GIB = 1;
export const MAX_VOLUME_GIB = 1024;
export const MAX_VOLUMES_PER_CONTAINER = 8;
export const MAX_VOLUME_NAME_LENGTH = 63;
export const MAX_MOUNT_PATH_LENGTH = 1024;

/** DNS-1123 label. */
const NAME_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

const RESERVED_MOUNT_PATHS = [
    '/',
    '/bin',
    '/boot',
    '/dev',
    '/etc',
    '/lib',
    '/lib64',
    '/proc',
    '/root',
    '/run',
    '/sbin',
    '/sys',
    '/usr',
    '/var/run',
];

export type VolumeErrorKey =
    | 'nameRequired'
    | 'namePattern'
    | 'nameLength'
    | 'pathRequired'
    | 'pathAbsolute'
    | 'pathTraversal'
    | 'pathReserved'
    | 'pathLength'
    | 'sizeRange'
    | 'sizeGrowOnly';

/**
 * English fallbacks passed to t() as the default value, so a missing key still renders a
 * sentence. Keep in sync with volumes.validation.* in
 * src/i18n/locales/{en,th}/containerEngineInstance.json.
 */
export const VOLUME_ERROR_DEFAULTS: Record<VolumeErrorKey, string> = {
    nameRequired: 'Volume name is required.',
    namePattern:
        'Use lowercase letters, digits and hyphens, starting and ending with a letter or digit.',
    nameLength: `Name must be at most ${MAX_VOLUME_NAME_LENGTH} characters.`,
    pathRequired: 'Mount path is required.',
    pathAbsolute: 'Mount path must start with "/".',
    pathTraversal: 'Mount path cannot contain "..".',
    pathReserved: 'That mount path is reserved by the system.',
    pathLength: `Mount path must be at most ${MAX_MOUNT_PATH_LENGTH} characters.`,
    sizeRange: `Size must be between ${MIN_VOLUME_GIB} and ${MAX_VOLUME_GIB} GiB.`,
    sizeGrowOnly: 'Volumes can only grow. Enter a size larger than the current one.',
};

/** Port of Go's path.Clean, so client and server agree on what a path resolves to. */
export function cleanMountPath(input: string): string {
    const isAbsolute = input.startsWith('/');
    const resolved: string[] = [];

    for (const segment of input.split('/')) {
        if (segment === '' || segment === '.') continue;
        if (segment === '..') {
            if (resolved.length > 0) resolved.pop();
            else if (!isAbsolute) resolved.push('..');
            continue;
        }
        resolved.push(segment);
    }

    if (isAbsolute) return `/${resolved.join('/')}`;
    return resolved.length === 0 ? '.' : resolved.join('/');
}

export function validateVolumeName(name: string): VolumeErrorKey | null {
    const trimmed = name.trim();
    if (!trimmed) return 'nameRequired';
    if (trimmed.length > MAX_VOLUME_NAME_LENGTH) return 'nameLength';
    if (!NAME_PATTERN.test(trimmed)) return 'namePattern';
    return null;
}

export function validateMountPath(mountPath: string): VolumeErrorKey | null {
    const trimmed = mountPath.trim();
    if (!trimmed) return 'pathRequired';
    if (!trimmed.startsWith('/')) return 'pathAbsolute';

    const cleaned = cleanMountPath(trimmed);
    if (cleaned.includes('..')) return 'pathTraversal';
    if (cleaned.length > MAX_MOUNT_PATH_LENGTH) return 'pathLength';
    if (RESERVED_MOUNT_PATHS.includes(cleaned)) return 'pathReserved';
    return null;
}

export function validateVolumeSize(sizeGiB: number): VolumeErrorKey | null {
    if (!Number.isInteger(sizeGiB) || sizeGiB < MIN_VOLUME_GIB || sizeGiB > MAX_VOLUME_GIB) {
        return 'sizeRange';
    }
    return null;
}

/** Resize is grow-only server-side; an equal size is rejected too. */
export function validateResize(next: number, current: number): VolumeErrorKey | null {
    const range = validateVolumeSize(next);
    if (range) return range;
    if (next <= current) return 'sizeGrowOnly';
    return null;
}
